import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';

import { openModal, closeModal } from 'actions/modal';
import { MODAL_FORM } from 'constants/form';
import { patchRegionPayoutBatch } from 'actions/region-payout';
import { ratioLabel } from './constants';
import { cellKey, notify } from './utils';

// Owns the region-wide "edit end date" flow: gather the windows the grid shows for the
// active region + ratio, open the modal, and apply one atomic batch PATCH.
export const useRegionEditEnd = ({
  activeRegion,
  classRatio,
  cellData,
  visibleGradeRanges,
  visibleTiers,
  fetchPayouts,
}) => {
  const dispatch = useDispatch();

  // Representative window per visible cell for the active region + ratio — the rows a
  // region-level end-date edit will touch (one per grade · tier shown in the grid).
  const regionWindows = useCallback(() => {
    const out = [];
    visibleGradeRanges.forEach(grade => {
      visibleTiers.forEach(tier => {
        const entry = cellData[cellKey(activeRegion, grade, tier)];
        if (entry) out.push(entry.row);
      });
    });
    return out;
  }, [visibleGradeRanges, visibleTiers, cellData, activeRegion]);

  // Set the same validity_end on every active window in the region in ONE atomic
  // request. It is all-or-nothing: if any window is frozen or the new end would overlap
  // a neighbour, the server rejects the whole batch and nothing changes, so the grid is
  // never left half-updated. On failure we return the error and leave the modal open
  // (with its inline message) so the user can pick a different date.
  const submitRegionEditEnd = async newEnd => {
    const windows = regionWindows();
    if (!windows.length) {
      return { ok: false, error: 'No active windows to update.' };
    }
    const res = await patchRegionPayoutBatch(
      windows.map(w => w.id),
      {
        validity_end: newEnd,
      },
    );
    if (!res.ok) return res;
    dispatch(closeModal());
    fetchPayouts();
    notify(
      dispatch,
      `Updated end date on ${windows.length} window${windows.length > 1 ? 's' : ''}.`,
    );
    return { ok: true };
  };

  const openRegionEditEnd = () => {
    const windows = regionWindows();
    if (!windows.length) {
      notify(dispatch, 'No active windows to update for this region and ratio.');
      return;
    }
    // New end must clear every window's valid-from, so anchor on the latest start.
    const latestStart = windows.reduce(
      (latest, w) =>
        moment(w.validity_start).isAfter(moment(latest)) ? w.validity_start : latest,
      windows[0].validity_start,
    );
    dispatch(
      openModal(
        MODAL_FORM.REGION_PAYOUT_EDIT_END_MODAL,
        { size: 'custom', width: '440px', height: 'fit-content' },
        {
          region: activeRegion,
          ratio: ratioLabel(classRatio),
          count: windows.length,
          latestStart,
          onSubmit: newEnd => submitRegionEditEnd(newEnd),
        },
      ),
    );
  };

  return { openRegionEditEnd };
};
